import dayjs from 'dayjs';
import { GenreCollection } from '@/modules/Collection';
import { _Program } from '@/modules/Program';
import { DailySchedule } from '@/modules/Schedule';
import { FuturePrograms } from './useProgramWorker';

export const useProgram = () => {
  /**
   * - 番組が開始しているか判定
   */
  const isStarted = (program: _Program, now: Date) => {
    const programStartTime = dayjs(program.start).toDate();

    return programStartTime.getTime() <= now.getTime();
  };

  /**
   * 番組が終了しているか
   */
  const isEnded = (program: _Program, now: Date) => {
    const programEndTime = dayjs(program.end).toDate();

    return programEndTime.getTime() <= now.getTime();
  };

  // NOTE: ProgramWorker内で使う関数

  /**
   * 同じ放送回が既に含まれているかの判定
   * 時間とチャンネル情報が一致したら重複と判断
   */
  const _isDuplicated = (currentProgram: _Program, currentProgramsArray: _Program[]) => {
    const result = currentProgramsArray.find((program) => {
      return (
        dayjs(program.end).toDate().getTime() == dayjs(currentProgram.end).toDate().getTime() &&
        program.channel === currentProgram.channel
      );
    });

    return !!result;
  };

  /**
   * yyyymm.jsonデータから重複・過去放送を除いた番組ID、番組配列のMapデータを返す関数
   * @return Map<string, _Program[]>  <番組ID,番組配列>
   */
  const getValidProgramsFromSchedule = (
    confirmedScheduleData: {
      schedule: null | Record<string, DailySchedule>;
    },
    futurePrograms: FuturePrograms,
    now: Date
  ) => {
    const result: Map<string, _Program[]> = new Map();

    Object.keys(confirmedScheduleData.schedule || {}).forEach((key) => {
      if (!confirmedScheduleData.schedule) return;

      /** 重複、放送時間チェックを行い、問題がなければ番組を追加する関数 */
      const _updateResult = (program: _Program) => {
        const currentProgramsArray = [
          ...(futurePrograms.prevMonth.get(program.id) || []),
          ...(futurePrograms.thisMonth.get(program.id) || []),
          ...(futurePrograms.nextMonth.get(program.id) || []),
          ...(result.get(program.id) || []),
        ];

        if (isEnded(program, now) || _isDuplicated(program, currentProgramsArray)) return;

        if (result.has(program.id)) {
          result.get(program.id)?.push(program);
          return;
        }

        result.set(program.id, [program]);
      };

      confirmedScheduleData.schedule[key]?.tch1.forEach((program) => {
        _updateResult(program);
      });

      confirmedScheduleData.schedule[key]?.tch2.forEach((program) => {
        _updateResult(program);
      });
    });

    return result;
  };

  /**
   * ~~existence.jsonデータから重複・過去放送を除いた番組ID、番組配列のMapデータを返す関数
   * @return Map<string, _Program[]>  <番組ID,番組配列>
   */
  const getValidProgramsFromCollections = (
    unConfirmedScheduleData: { collections: Omit<GenreCollection, 'title'>[] },
    targetMonthPrograms: FuturePrograms['thisMonth' | 'nextMonth'],
    now: Date
  ) => {
    const result: Map<string, _Program[]> = new Map();

    /**
     * trueになる条件
     * - targetMonthPrograms（thisMonth | NextMonth）に同じ番組ID、同じチャンネルの番組が既に入っている場合
     * - この関数で作っているMapデータに同じチャンネルの番組が入っている場合
     * - 引数で受けた番組の開始時刻が1日4:00以前に開始する場合（前の月の番組データに入っている為）
     */
    const _isAdded = (targetProgram: _Program) => {
      return (
        !!targetMonthPrograms.get(targetProgram.id)?.find((program) => program.channel === targetProgram.channel) ||
        !!result.get(targetProgram.id)?.find((program) => program.channel === targetProgram.channel) ||
        (dayjs(targetProgram.start).hour() < 4 && dayjs(targetProgram.start).date() === 1)
      );
    };

    unConfirmedScheduleData.collections.forEach((collection) => {
      collection.items.forEach((program) => {
        if (_isAdded(program) || isEnded(program, now)) return;

        if (result.has(program.id)) {
          result.get(program.id)?.push(program);
          return;
        }

        result.set(program.id, [program]);
      });
    });

    return result;
  };

  /**
   * thisMonthの最後に番組がある
   * nextExistenceに同じ番組がある
   *
   * thisExistenceを作るときに見るのはthisMonth
   * nextExistenceを作るときに見るのはnextMonth
   *
   * 今月末に番組がある
   * 来月未確定に番組がある
   *
   * どちらも入ってしまう
   *
   * 4時より前に始まる番組を弾けばいい？
   */

  /**
   * 番組配列をstart時間でソートする関数
   * @returns
   */
  const sortProgramByStartTime = (programs: _Program[]) => {
    return programs.sort((a, b) => dayjs(a.start).toDate().getTime() - dayjs(b.start).toDate().getTime()) || [];
  };

  return { isStarted, isEnded, getValidProgramsFromSchedule, getValidProgramsFromCollections, sortProgramByStartTime };
};
